import { computed, onBeforeUnmount, onMounted, readonly, ref, type ComputedRef } from 'vue';

/**
 * 표준 BP(px) — tailwind.config.ts theme.screens 와 1:1 일치.
 * JS 분기와 CSS 미디어쿼리의 BP 일치를 단일 소스로 보장한다.
 */
export const BREAKPOINTS = {
  sm: 380,
  md: 600,
  lg: 980,
  xl: 1080,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * 뷰포트 너비를 추적해 `min-width: {bp}px` 충족 여부를 반응형으로 제공한다.
 * - SSR/비브라우저 환경에서도 안전(초기값 0, mount 후 평가).
 * - resize 는 rAF 로 스로틀, onBeforeUnmount 에서 리스너·rAF 정리.
 *
 * matches() 는 **ComputedRef 를 반환**한다 — 호출부가 일반 변수에 담아도 반응성을
 * 잃지 않도록(함수 반환이면 `const x = matches('lg')` 시 정적 스냅샷이 되는 함정 제거).
 *
 * @example
 *   const { matches } = useBreakpoint();
 *   const isDesktop = matches('lg'); // ComputedRef<boolean> — 템플릿에서 isDesktop 로 사용
 */
export function useBreakpoint() {
  // 브라우저면 첫 렌더부터 실제 너비로 평가한다(초기값 0 → mount 후 갱신 사이의 1프레임
  // 레이아웃 시프트 제거). v-if=isDesktop 분기가 마운트 직후 바뀌며 히어로 높이가 뒤늦게
  // 늘어나던 race 를 차단 — 해시 앵커(/#price 등) 진입 정확도의 전제. SSR/비브라우저는 0.
  const width = ref(typeof window === 'undefined' ? 0 : window.innerWidth);

  let raf = 0;
  function onResize() {
    if (raf) return;
    raf = window.requestAnimationFrame(() => {
      raf = 0;
      width.value = window.innerWidth;
    });
  }

  onMounted(() => {
    width.value = window.innerWidth;
    window.addEventListener('resize', onResize, { passive: true });
  });

  onBeforeUnmount(() => {
    if (raf) window.cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
  });

  /** `min-width: BREAKPOINTS[bp]`(>=) 충족 여부 — 반응형 ComputedRef. */
  function matches(bp: Breakpoint): ComputedRef<boolean> {
    return computed(() => width.value >= BREAKPOINTS[bp]);
  }

  return {
    width: readonly(width),
    matches,
  };
}
